import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { getApiErrorMessage, logApiError } from 'services/apiError'
import viaCepApi from 'services/viaCepApi'
import MaskedInputService from 'services/maskedInput'
import NumberInput from 'components/Inputs/NumberInput'
import Select from 'components/Inputs/Select'
import AlertDialog from 'components/AlertDialog'
import MainButton from 'components/Buttons/MainButton'
import SecondaryButton from 'components/Buttons/SecondaryButton'
import ChangeProfessionalPictureModal from 'pages/Modals/ChangeProfessionalPictureModal'
import editProfessionalValidator from 'validators/editProfessionalValidator'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { FaStar } from 'react-icons/fa'
import ReactTooltip from 'react-tooltip'
import { Formik } from 'formik'
import { DateTime } from 'luxon'

import {
  Container,
  Form,
  BigImage,
  ImageContainer,
  RatingContainer,
  AddressContainer,
  ButtonContainer,
  SubTitle,
  LinkText,
  FormField,
  Label,
  WarningText,
  ErrorContainer,
  LoadingBackdrop,
  Input,
  MaskedInput,
  Row
} from '../styles'

export default function EditProfessionalPersonalInfo ({
  professional,
  onPictureChange: handlePictureChange = () => {}
}) {
  const [loadingAddress, setLoadingAddress] = useState (false)
  const [addressFound, setAddressFound] = useState (false)
  const [errorMessage, setErrorMessage] = useState (null)
  const [showPictureDialog, setShowPictureDialog] = useState (false)
  const [showDeleteDialog, setShowDeleteDialog] = useState (false)
  const [showRestoreDialog, setShowRestoreDialog] = useState (false)
  const [deleting, setDeleting] = useState (false)
  const [restoring, setRestoring] = useState (false)
  const [showOkDialog, setShowOkDialog] = useState (false)
  const token = AuthService.getToken ()
  const businessId = AuthService.getBusinessId ()
  const hasEditPermission = AuthService.hasPermission ('business_config.professionals', 'edit')
  const hasDeletePermission = AuthService.hasPermission ('business_config.professionals', 'delete')
  const navigate = useNavigate ()

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  const searchAddress = async (event, setFieldValue) => {
    const postal_code = event.target.rawValue

    setFieldValue ('postal_code', postal_code)
    
    if (postal_code.length === 8) {
      setLoadingAddress (true)
      try {
        const response = await viaCepApi.get (`/ws/${postal_code}/json`)
        const { data } = response

        setFieldValue ('route', data.logradouro)
        setFieldValue ('district', data.bairro)
        setFieldValue ('city', data.localidade)
        setFieldValue ('state', data.uf)

        setAddressFound (true)
      } catch (error) {
        logApiError (error)
      } finally {
        setLoadingAddress (false)
      }
    }
  }

  /*
    A API recusa excluir quem ainda tem agendamento marcado, e a mensagem diz quantos e o que
    fazer. Sem mostrá-la, o botão só parava de carregar e o profissional continuava ali.
  */
  const deleteProfessional = async () => {
    try {
      setErrorMessage(null)
      setDeleting (true)

      await cutileiApi.delete (
        `/businesses/${businessId}/professionals/${professional?.id}`,
        requestConfig
      )

      setDeleting (false)
      navigate ('/business', { replace : true })
    } catch (error) {
      setDeleting (false)
      setErrorMessage(getApiErrorMessage(error))
      logApiError (error)
    }
  }

  const restoreProfessional = async () => {
    try {
      setRestoring (true)

      await cutileiApi.post (
        `/businesses/${businessId}/professionals/${professional.id}/restore`,
        {},
        requestConfig
      )
      
      setRestoring (false)
    } catch (error) {
      setRestoring (false)
      logApiError (error)
    }
  }

  const togglePictureDialog = () => setShowPictureDialog (!showPictureDialog)
  const toggleDeleteDialog = () => setShowDeleteDialog (!showDeleteDialog)
  const toggleRestoreDialog = () => setShowRestoreDialog (!showRestoreDialog)
  const toggleOkDialog = () => setShowOkDialog (!showOkDialog)

  return (
    <Container>
      <Formik
        validationSchema={editProfessionalValidator}
        validateOnChange={false}
        validateOnBlur={true}
        initialValues={{
          'id': professional?.id,
          'name': professional?.name,
          'nickname': professional?.nickname || '',
          'cpf': professional?.cpf,
          'order': professional?.number,
          'email': professional?.email,
          'birth': DateTime.fromISO (professional?.birth).toFormat ('ddMMyyyy'),
          'gender': {label: professional?.gender, value: professional?.gender},
          'phone': professional?.phone,
          'route': professional?.addresses[0].route,
          'number': professional?.addresses[0].number,
          'complement': professional?.addresses[0].complement,
          'district': professional?.addresses[0].district,
          'city': professional?.addresses[0].city,
          'state': professional?.addresses[0].state,
          'postal_code': professional?.addresses[0].postal_code
        }}
        onSubmit={async (values, { setSubmitting }) => {
          const {
            name,
            nickname,
            cpf,
            rg,
            order,
            email,
            birth,
            gender,
            phone,
            ...address
          } = values

          setErrorMessage (null)
          setSubmitting (true)

          try {
            await cutileiApi.put (`/businesses/${businessId}/professionals/${professional?.id}`, {
              name,
              nickname,
              cpf,
              rg,
              email,
              number: order,
              birth: DateTime.fromFormat (birth, 'ddMMyyyy').toISODate (),
              gender: gender.value,
              phone,
              address: { ...address, id: professional?.addresses[0].id}
            }, requestConfig)

            toggleOkDialog ()
            setSubmitting (false)
          } catch (error) {
            setSubmitting (false)
            logApiError (error)
          }
        }}
      >
        {({
          dirty,
          values,
          errors,
          touched,
          isSubmitting,
          setFieldValue,
          setFieldTouched,
          handleChange,
          handleBlur,
          handleSubmit
        }) => (
          <Form>
            <SubTitle>Dados pessoais</SubTitle>
            <ImageContainer onClick={togglePictureDialog}>
              <BigImage src={professional?.profile_picture}/>
              {professional?.ratings_count > 0 && (
                <RatingContainer>
                  <FaStar size={16} color='#FFC100'/>
                  <SubTitle>
                    {professional?.average_rating}
                  </SubTitle>
                </RatingContainer>
              )}
            </ImageContainer>
            <Row>
              {professional?.ratings_count !== 0 && (
                <LinkText
                  to={`/business/professionals/${professional?.id}/ratings`}
                  state={{ professional }}
                >
                  {`${professional?.ratings_count} ${professional?.ratings_count === 1 ? 'avaliação' : 'avaliações'}`}
                </LinkText>
              )}
            </Row>
            <Row>
              <FormField stretch={2}>
                <Input
                  placeholder='Nome completo'
                  value={values.name}
                  onChange={handleChange ('name')}
                  onBlur={handleBlur ('name')}
                  disabled={!hasEditPermission}
                />
                {touched.name && errors.name && (
                  <WarningText>
                    {errors.name}
                  </WarningText>
                )}
              </FormField>
              <FormField>
                <Input
                  placeholder='Apelido'
                  value={values.nickname}
                  onChange={handleChange ('nickname')}
                  onBlur={handleBlur ('nickname')}
                  disabled={!hasEditPermission}
                  data-for='nickname'
                  data-tip
                />
                <ReactTooltip id='nickname' effect='solid' backgroundColor='#252525' tooltipRadius='10'>
                  <Label style={{color: '#FFFFFF'}}>
                    Este é o nome que aparecerá na agenda do salão e para os clientes. <br/>
                    Se o campo não for preenchido, o primeiro nome será usado.
                  </Label>
                </ReactTooltip>
                {touched.nickname && errors.nickname && (
                  <WarningText>
                    {errors.nickname}
                  </WarningText>
                )}
              </FormField>
              <FormField>
                <MaskedInput
                  name='cpf'
                  placeholder='CPF'
                  options={{
                    blocks: [3,3,3,2],
                    delimiters: ['.', '.', '-'],
                    numericOnly: true
                  }}
                  value={values.cpf}
                  onChange={event => MaskedInputService.handleChange (event, setFieldValue)}
                  onBlur={handleBlur ('cpf')}
                  disabled={!hasEditPermission}
                />
                {touched.cpf && errors.cpf && (
                  <WarningText>
                    {errors.cpf}
                  </WarningText>
                )}
              </FormField>
              <FormField>
                <NumberInput
                  name='order'
                  placeholder='Ordem'
                  value={values.order}
                  minValue={1}
                  onChange={setFieldValue}
                  disabled={!hasEditPermission}
                  style={{width: '100%'}}
                />
              </FormField>
            </Row>
            <Row>
              <FormField stretch={2}>
                <Input
                  type='email'
                  autoComplete='off'
                  placeholder='E-mail'
                  value={values.email}
                  onChange={handleChange ('email')}
                  onBlur={handleBlur ('email')}
                  disabled={!hasEditPermission}
                />
                {touched.email && errors.email && (
                  <WarningText>
                    {errors.email}
                  </WarningText>
                )}
              </FormField>
              <FormField>
                <MaskedInput
                  name='birth'
                  placeholder='Nascimento'
                  options={{date: true, datePattern: ['d', 'm', 'Y']}}
                  value={values.birth}
                  onChange={event => MaskedInputService.handleChange (event, setFieldValue)}
                  onBlur={handleBlur ('birth')}
                  disabled={!hasEditPermission}
                />
                {touched.birth && errors.birth && (
                  <WarningText>
                    {errors.birth}
                  </WarningText>
                )}
              </FormField>
              <FormField>
                <Select
                  name='gender'
                  placeholder='Gênero...'
                  value={values.gender}
                  options={[
                    {label: 'Masculino', value: 'Masculino'},
                    {label: 'Feminino', value: 'Feminino'},
                    {label: 'Não informar', value: 'Não informar'}
                  ]}
                  onChange={setFieldValue}
                  onBlur={setFieldTouched}
                  error={errors.gender}
                  touched={touched.gender}
                  isDisabled={!hasEditPermission}
                />
                {touched.gender && errors.gender && (
                  <WarningText>
                    {errors.gender}
                  </WarningText>
                )}
              </FormField>
              <FormField>
                <MaskedInput
                  name='phone'
                  placeholder='Celular'
                  options={{
                    blocks: [0, 2, 5, 4],
                    delimiters: ['(', ') ', '-'],
                    numericOnly: true,
                  }}
                  value={values.phone}
                  onChange={event => MaskedInputService.handleChange (event, setFieldValue)}
                  onBlur={handleBlur ('phone')}
                  disabled={!hasEditPermission}
                />
                {touched.phone && errors.phone && (
                  <WarningText>
                    {errors.phone}
                  </WarningText>
                )}
              </FormField>
            </Row>
            <SubTitle>Endereço</SubTitle>
            <AddressContainer>
              {loadingAddress && (
                <LoadingBackdrop>
                  <Loading/>
                </LoadingBackdrop>
              )}
              <Row>
                <FormField>
                  <MaskedInput
                    name='postal_code'
                    placeholder='CEP'
                    options={{
                      blocks: [5,3],
                      delimiters: ['-'],
                      numericOnly: true
                    }}
                    value={values.postal_code}
                    onChange={event => searchAddress (event, setFieldValue)}
                    onBlur={handleBlur ('postal_code')}
                    disabled={!hasEditPermission}
                  />
                  {touched.postal_code && errors.postal_code && (
                    <WarningText>
                      {errors.postal_code}
                    </WarningText>
                  )}
                </FormField>
                <FormField stretch={2}>
                  <Input
                    placeholder='Rua/avenida'
                    value={values.route}
                    onChange={handleChange ('route')}
                    onBlur={handleBlur ('route')}
                    disabled
                  />
                </FormField>
                <FormField>
                  <Input
                    placeholder='Número'
                    value={values.number}
                    onChange={handleChange ('number')}
                    onBlur={handleBlur ('number')}
                    disabled={!hasEditPermission}
                  />
                  {touched.number && errors.number && (
                    <WarningText>
                      {errors.number}
                    </WarningText>
                  )}
                </FormField>
                <FormField>
                  <Input
                    placeholder='Complemento'
                    value={values.complement}
                    onChange={handleChange ('complement')}
                    onBlur={handleBlur ('complement')}
                    disabled={!hasEditPermission}
                  />
                </FormField>
              </Row>
              <Row>
                <FormField stretch={4}>
                  <Input
                    placeholder='Bairro'
                    value={values.district}
                    onChange={handleChange ('district')}
                    onBlur={handleBlur ('district')}
                    disabled
                  />
                </FormField>
                <FormField stretch={3}>
                  <Input
                    placeholder='Cidade'
                    value={values.city}
                    onChange={handleChange ('city')}
                    onBlur={handleBlur ('city')}
                    disabled
                  />
                </FormField>
                <FormField>
                  <Input
                    placeholder='Estado'
                    value={values.state}
                    onBlur={handleBlur ('state')}
                    disabled
                  />
                </FormField>
              </Row>
            </AddressContainer>
            {errorMessage && (
              <ErrorContainer>
                <p>{errorMessage}</p>
              </ErrorContainer>
            )}
            <ButtonContainer>
              {hasEditPermission && dirty && (
                <MainButton
                  label='Salvar alterações'
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  loading={isSubmitting}
                  width={170}
                  style={{alignSelf: 'center'}}
                />
              )}
              {hasDeletePermission && !professional.deleted && (
                <MainButton
                  label='Excluir profissional'
                  onClick={toggleDeleteDialog}
                  dangerous={true}
                  disabled={deleting}
                  loading={deleting}
                  width={170}
                  style={{alignSelf: 'center'}}
                />
              )}
              {hasEditPermission && professional.deleted && (
                <SecondaryButton
                  label='Restaurar profissional'
                  onClick={toggleRestoreDialog}
                  disabled={restoring}
                  loading={restoring}
                  width={170}
                  style={{alignSelf: 'center'}}
                />
              )}
            </ButtonContainer>
          </Form>
        )}
      </Formik>
      <ChangeProfessionalPictureModal
        visible={showPictureDialog}
        professional={professional}
        onConfirm={handlePictureChange}
        onClose={togglePictureDialog}
      />
      <AlertDialog
        visible={showDeleteDialog}
        title='Atenção!'
        message='Deseja realmente excluir este profissional?'
        confirmText='Sim'
        cancelText='Não'
        onConfirm={deleteProfessional}
        onClose={toggleDeleteDialog}
        dangerous={true}
      />
      <AlertDialog
        visible={showRestoreDialog}
        title='Atenção!'
        message='Deseja realmente restaurar este profissional?'
        confirmText='Sim'
        cancelText='Não'
        onConfirm={restoreProfessional}
        onClose={toggleRestoreDialog}
      />
      <AlertDialog
        visible={showOkDialog}
        title='Sucesso'
        message='Alterações salvas!'
        confirmText='Ok'
        onConfirm={toggleOkDialog}
        onClose={toggleOkDialog}
      />
    </Container>
  )
}
